import { sanitizeHtml } from "utils/sanitizeHtml";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import imgFallback from "images/designB/imgsection.jpg";
import img1Fallback from "images/designB/imgsection1.png";
import img2Fallback from "images/designB/imgsection2.png";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { useInView } from "designs/DesignB/hooks/useInView";

type Panel = {
  image_url?: string | null;
  heading?: string | null;
  description?: string | null;
  link?: string | null;
};

const FALLBACK_IMAGES = [imgFallback, img1Fallback, img2Fallback];

function ImageSection() {
  const { ourVehicles, imageSection } = useData();
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const heroEntrance = useInView<HTMLDivElement>();
  const subPanelsEntrance = useInView<HTMLDivElement>();

  // Order panels by `index` so the admin's drag-sort applies; fall back to
  // the order the API returned. Pad to 3 so the layout never collapses.
  const panels: Panel[] = useMemo(() => {
    const sorted = [...(imageSection?.keypoints ?? [])].sort(
      (a: any, b: any) => (a.index ?? 0) - (b.index ?? 0),
    );
    while (sorted.length < 3) sorted.push({});
    return sorted.slice(0, 3) as Panel[];
  }, [imageSection]);

  const heroPanel = panels[0];
  const heroDescription = heroPanel?.description ?? "";
  const isTruncated = heroDescription.length > 500;
  const shortDescription = isTruncated
    ? heroDescription.slice(0, 500)
    : heroDescription;

  const brands = useMemo(() => {
    const seen = new Set<string>();
    return ourVehicles
      .filter((vehicle) => vehicle.fleet_class && vehicle.fleet_class.name)
      .map((vehicle) => {
        const name = vehicle.fleet_class?.name?.trim() || "";
        if (seen.has(name.toLowerCase())) return null;
        seen.add(name.toLowerCase());
        return { name, isDefault: false };
      })
      .filter(Boolean) as { name: string; logo: string; isDefault: boolean }[];
  }, [ourVehicles]);

  const goTo = (link?: string | null) => {
    if (!link) return;
    if (/^https?:\/\//i.test(link)) {
      window.open(link, "_blank", "noopener");
    } else {
      navigate(link);
    }
  };

  return (
    <>
      {/* ================ HERO PANEL ================ */}
      <div
        ref={heroEntrance.ref}
        className={`group relative w-full h-[600px] md:h-[650px] lg:h-[700px] xl:h-[800px] overflow-hidden rounded-3xl
          transform-gpu transition-all duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform
          ${
            heroEntrance.inView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-12"
          }`}
      >
        {/* Image with subtle kenburns — runs continuously, intensifies slightly on hover. */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={heroPanel?.image_url || FALLBACK_IMAGES[0]}
            alt={heroPanel?.heading || "Image section hero"}
            className="w-full h-full object-cover scale-105 transition-transform duration-[14000ms] ease-out group-hover:scale-[1.12]"
          />
          {/* Gradient overlay — original framing for legibility, plus a soft
              top vignette on hover for depth. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0) 7.96%, rgba(0, 0, 0, 0.916567) 76.44%, #000000 82.67%)",
            }}
          />
          <div className="absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100 bg-gradient-to-b from-black/30 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-10 lg:p-16">
          {/* Heading — slides up after the card itself. */}
          <div
            className={`max-w-3xl mt-auto pt-20 transform-gpu transition-all duration-[900ms] delay-200 ease-[cubic-bezier(0.16,1,0.3,1)]
              ${
                heroEntrance.inView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
          >
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6 lg:mb-8 drop-shadow-2xl
                [&_p]:m-0 [&_p]:inline [&_strong]:font-extrabold [&_em]:italic"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(heroPanel?.heading || "") }}
            />
          </div>

          <div>
            {/* Brand pills — staggered cascade on entrance, lift + glow on hover. */}
            {brands.length > 0 && (
              <div className="flex flex-wrap gap-2.5 sm:gap-3 mb-6 sm:mb-8">
                {brands.slice(0, 6).map((brand, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => navigate("/vehicles?make=" + brand.name)}
                    style={{
                      transitionDelay: heroEntrance.inView
                        ? `${500 + index * 90}ms`
                        : "0ms",
                    }}
                    className={`relative px-5 sm:px-6 py-2 sm:py-2.5 bg-primary-100/95 text-text-color font-medium text-sm sm:text-base rounded-full cursor-pointer
                      transform-gpu transition-all duration-500 ease-out
                      hover:bg-primary-200 hover:text-on-primary hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary-200/40 active:scale-95
                      ${
                        heroEntrance.inView
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-4"
                      }`}
                  >
                    {brand.name}
                  </button>
                ))}
              </div>
            )}

            {/* Description with view-more toggle */}
            {heroDescription && (
              <div
                className={`transform-gpu transition-all duration-700 delay-[700ms] ease-out
                  ${
                    heroEntrance.inView
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
              >
                <span className="block !font-normal !text-md text-white/90 break-words text-wrap leading-relaxed
                  [&_p]:mb-2 last:[&_p]:mb-0
                  [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                  [&_strong]:font-semibold [&_em]:italic [&_u]:underline">
                  {expanded || !isTruncated ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(heroDescription) }}
                    />
                  ) : (
                    <>
                      <span
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(shortDescription) }}
                      />
                      ...
                    </>
                  )}
                  {isTruncated && (
                    <button
                      type="button"
                      className="ml-2 text-primary-100 hover:text-primary-200 font-semibold focus:outline-none inline transition-colors duration-200"
                      onClick={() => setExpanded((v) => !v)}
                      style={{ display: "inline" }}
                    >
                      {expanded ? "View less" : "View more"}
                    </button>
                  )}
                </span>
              </div>
            )}

            {/* Hero CTA — gradient bg, arrow nudge on hover, lift, glow. */}
            {heroPanel?.link && (
              <button
                type="button"
                onClick={() => goTo(heroPanel.link)}
                style={{
                  transitionDelay: heroEntrance.inView ? "850ms" : "0ms",
                }}
                className={`group/cta mt-5 sm:mt-6 inline-flex items-center gap-2 bg-primary-100 hover:bg-primary-200 text-text-color hover:text-on-primary px-6 sm:px-7 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base
                  transform-gpu transition-all duration-500 ease-out
                  hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary-200/40 active:scale-95
                  ${
                    heroEntrance.inView
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
              >
                Learn more
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ================ SUB PANELS ================ */}
      <div
        ref={subPanelsEntrance.ref}
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8"
      >
        {panels.slice(1, 3).map((panel, index) => (
          <article
            key={index}
            style={{
              transitionDelay: subPanelsEntrance.inView
                ? `${index * 150}ms`
                : "0ms",
            }}
            className={`group/sub relative rounded-3xl overflow-hidden h-[320px] cursor-pointer
              transform-gpu transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]
              hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-black/30
              ${
                subPanelsEntrance.inView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-12"
              }`}
            onClick={() => panel?.link && goTo(panel.link)}
          >
            {/* Image with hover zoom */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={panel?.image_url || FALLBACK_IMAGES[index + 1]}
                alt={panel?.heading || `Panel ${index + 2}`}
                className="w-full h-full object-cover transform-gpu transition-transform duration-[1200ms] ease-out group-hover/sub:scale-110"
              />
              {/* Layered gradients — base for legibility + a hover layer that
                  deepens slightly so text always stays readable as the image
                  zooms and brightens. */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover/sub:opacity-100" />
            </div>

            {/* Subtle accent ring — fades in on hover */}
            <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/0 transition-all duration-500 group-hover/sub:ring-primary-200/40" />

            <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-7 lg:p-8">
              <h2 className="text-base md:text-lg lg:text-xl font-bold text-white drop-shadow-lg leading-tight max-w-[80%]">
                {panel?.heading || ""}
              </h2>

              {panel?.description && (
                <p className="text-sm text-white/85 line-clamp-2 max-w-md leading-relaxed">
                  {panel.description}
                </p>
              )}

              {panel?.link && (
                <div className="self-end">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      goTo(panel.link);
                    }}
                    className="group/btn flex items-center gap-1.5 bg-white/10 hover:bg-white text-white hover:text-text-color border border-white/40 hover:border-white px-5 py-2 rounded-full text-xs sm:text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-xl hover:shadow-white/20 active:scale-95"
                  >
                    MORE
                    <ArrowUpRightIcon className="h-3.5 w-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </button>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

export default ImageSection;
